import { Box, DragHandle, Flex, Icon } from '@televet/kibble-ui';
import React, { ChangeEvent, MouseEvent } from 'react';
import { AvatarGroup, ClinicAvatar } from 'shared/components/Avatars';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import MoreOptionsButton from 'shared/components/MoreOptionsButton';
import Switch from 'shared/components/Switch';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { ClinicUserAvatarFragment, ConnectSettingsClinicWidgetRequestTypeFragment } from 'shared/types/graphql';
import { getClinicWidgetRequestTypeDisplayName, WidgetRequestTypeIconNames } from 'shared/utils/widgetRequests';
import styled from 'styled-components/macro';

export enum RequestTypeActions {
  Edit = 'edit',
  Delete = 'delete',
  Add = 'add',
}

const menuDropdownOptions = [
  {
    value: RequestTypeActions.Edit,
    text: 'Edit',
  },
  {
    value: RequestTypeActions.Delete,
    text: 'Delete',
  },
];

interface IRequestTypeDraggableRow {
  widgetRequestType: ConnectSettingsClinicWidgetRequestTypeFragment;
  index: number | undefined;
  hasPermission?: boolean;
  onPropertyToggleChanged: (
    e: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    index: number,
    property: keyof Pick<ConnectSettingsClinicWidgetRequestTypeFragment, 'isActive' | 'shouldCreateTask'>,
  ) => void;
  onMenuItemSelected: (e: MouseEvent, option: IOption, index: number) => void;
  displayTaskCreation?: boolean;
}

const RequestTypeDraggableRow = ({
  widgetRequestType,
  index = 0,
  hasPermission,
  onPropertyToggleChanged: onActiveToggleChanged,
  onMenuItemSelected,
  displayTaskCreation,
}: IRequestTypeDraggableRow): JSX.Element => {
  const requestTypeIconName = WidgetRequestTypeIconNames[widgetRequestType.requestType];
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();

  return (
    <Flex justify="space-between" align="center" gap={2}>
      <Box display="inline-block" h={8} w={8}>
        {hasPermission && <DragHandle />}
      </Box>

      <RequestTypesTableItem>{getClinicWidgetRequestTypeDisplayName(widgetRequestType)}</RequestTypesTableItem>
      <RequestTypesTableItem>{requestTypeIconName && <Icon name={requestTypeIconName} />}</RequestTypesTableItem>
      <RequestTypesTableItem>{widgetRequestType.channelStatus?.name}</RequestTypesTableItem>
      <RequestTypesTableItem className={hasPermission ? 'not_disabled' : 'disabled_switch'}>
        <AvatarGroup>
          {widgetRequestType.assignees?.map((assignee: ClinicUserAvatarFragment, index: number) => {
            return <ClinicAvatar key={assignee.id} clinicMember={assignee} isGrouped index={index} />;
          })}
        </AvatarGroup>
      </RequestTypesTableItem>
      <RequestTypesTableItem className={hasPermission ? 'not_disabled' : 'disabled_switch'}>
        <Switch
          disabled={!hasPermission}
          name={widgetRequestType.id}
          label=""
          checked={widgetRequestType.isActive || false}
          onChange={(e, checked): void => onActiveToggleChanged(e, checked, index, 'isActive')}
        />
      </RequestTypesTableItem>
      {!isFeatureFlagsLoading && isFeatureEnabled(FeatureFlagName.Tasks) && (
        <RequestTypesTableItem className={hasPermission ? 'not_disabled' : 'disabled_switch'}>
          {displayTaskCreation && (
            <Switch
              disabled={!hasPermission}
              name={`${widgetRequestType.id}-shouldCreateTask`}
              data-testid={`add-to-tasks-switch-${widgetRequestType.id}`}
              label=""
              checked={widgetRequestType.shouldCreateTask || false}
              onChange={(e, checked): void => onActiveToggleChanged(e, checked, index, 'shouldCreateTask')}
            />
          )}
        </RequestTypesTableItem>
      )}

      <Box display="inline-block" h={8} w={8} mt={-2} mr={2}>
        {hasPermission && (
          <Dropdown
            options={menuDropdownOptions}
            placement={PopoverPlacement.BottomEnd}
            onSelect={(e, option): void => onMenuItemSelected(e, option, index)}
          >
            <MoreOptionsButton />
          </Dropdown>
        )}
      </Box>
    </Flex>
  );
};

export default RequestTypeDraggableRow;

const RequestTypesTableItem = styled.div`
  font-size: 14px;
  flex: 1;
  z-index: 0;
  display: flex;
  align-items: center;

  svg {
    fill: var(--chakra-colors-text-default);
    width: 24px;
  }

  &:nth-of-type(2) {
    width: 220px;
    flex: 0 1 auto;
  }

  &.disabled_switch,
  &.disabled_switch > div > div > div,
  &.disabled_switch > div > div > input {
    cursor: not-allowed;
  }
`;
